import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { LogoBig } from "@/components/Logo";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(true);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPending(false);
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          setPending(false);
          return;
        }
      } catch {
        // réessaie
      }
      tries.current += 1;
      if (tries.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setPending(false);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-ink/5 sm:p-10"
      >
        <div className="mb-6 flex justify-center">
          <LogoBig />
        </div>

        {pending ? (
          <div data-testid="payment-pending">
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-forest" />
            <h1 className="font-serif text-2xl font-semibold text-ink">Confirmation de votre paiement…</h1>
            <p className="mt-2 text-ink/60">Quelques secondes suffisent.</p>
          </div>
        ) : result ? (
          <div data-testid="payment-success">
            <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-forest" />
            <h1 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">Merci, c'est réservé !</h1>
            <p className="mt-3 text-ink/65">
              Votre livraison est confirmée et un e-mail récapitulatif vient de vous être envoyé. Angélique vous
              appellera très vite au <span className="font-semibold text-ink">{result.booking?.phone}</span> pour
              valider le créneau et le tarif exact.
            </p>
            <dl className="mt-8 space-y-3 rounded-2xl bg-cream p-6 text-left text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/55">Référence</dt>
                <dd className="font-mono font-semibold text-ink" data-testid="booking-ref">{result.booking?.ref}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/55">Prestation</dt>
                <dd className="font-semibold text-ink">{result.booking?.delivery_label}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/55">Date</dt>
                <dd className="font-semibold text-ink">
                  {result.booking?.date} — {result.booking?.time_slot}
                </dd>
              </div>
              <div className="flex justify-between border-t border-ink/10 pt-3">
                <dt className="text-ink/55">Montant réglé</dt>
                <dd className="font-serif text-lg font-semibold text-forest">{eur(result.booking?.total ?? 0)}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-ink/50">
              Suivez votre livraison à tout moment avec votre référence{" "}
              <span className="font-semibold">{result.booking?.ref}</span>.
            </p>
            <Link
              to={`/suivi?ref=${result.booking?.ref}`}
              data-testid="track-booking-link"
              className="mt-6 inline-flex min-h-[48px] items-center rounded-full border border-forest/30 px-6 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream"
            >
              Suivre ma livraison
            </Link>
            <div className="mt-4" />
          </div>
        ) : (
          <div data-testid="payment-unconfirmed">
            <h1 className="font-serif text-2xl font-semibold text-ink">Paiement en cours de vérification</h1>
            <p className="mt-3 text-ink/60">
              La confirmation prend plus de temps que prévu. Si le montant a été débité, votre réservation est bien
              enregistrée : appelez-moi au 06 12 34 56 78 et je vérifie tout de suite.
            </p>
          </div>
        )}

        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-8 inline-flex min-h-[48px] items-center rounded-full bg-forest px-6 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark"
        >
          Retour à l'accueil
        </Link>
      </motion.div>
    </div>
  );
}
